import React, { useEffect, useState } from "react";
import { Button, Form, message, Tabs } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { firestore } from "../../../../configs/FirebaseConfig";
import { FIRESTORE_COUNTRIES_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../../constants/CompanyConstants";
import PageHeaderAlt from "../../../../components/layout-components/PageHeaderAlt";
import Flex from "../../../../components/shared-components/Flex";
import Loading from "../../../../components/shared-components/Loading";
import CompaniesField from "./CompaniesField";
import DatesField from "./DatesField";
import DeliveryField from "./DeliveryField";
import PricingField from "./PricingField";
import Utils from "../../../../utils";
import urls from "urls";

const { TabPane } = Tabs;

const ADD = "ADD";
const EDIT = "EDIT";
const VIEW = "VIEW";

const COUNTRY_NAME_KEYS = {
  NL: "netherlands",
  BE: "belgium",
  DE: "germany",
  FR: "france",
  LU: "luxembourg",
  CH: "switzerland",
};

const CountryForm = ({ mode = ADD, param }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(mode === EDIT || mode === VIEW);
  const countryCode = param?.countryCode ?? null;
  const countryName = COUNTRY_NAME_KEYS[countryCode] ? t(COUNTRY_NAME_KEYS[countryCode]) : countryCode;

  useEffect(() => {
    if ((mode === EDIT || mode === VIEW) && countryCode) {
      const loadCountry = async () => {
        try {
          const docRef = doc(
            firestore,
            FIRESTORE_REGIONS_TABLE,
            COMPANY_COUNTRY_CODE,
            FIRESTORE_COUNTRIES_TABLE,
            countryCode,
          );
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            // Ensure companies field exists (empty object if not present)
            if (!data.companies) {
              data.companies = {};
            }
            form.setFieldsValue(data);
          }
        } catch (err) {
          message.error(t("notifications.error_loading_country"));
        } finally {
          setDataLoading(false);
        }
      };
      loadCountry();
    }
  }, [mode, countryCode]);

  const onFinish = () => {
    form
      .validateFields()
      .then(async (values) => {
        setSubmitLoading(true);
        try {
          // Manually get companies data from form
          const companiesData = form.getFieldValue("companies") || {};

          // Filter out undefined values from companies
          const cleanedCompanies = {};
          Object.keys(companiesData).forEach((key) => {
            if (companiesData[key] !== undefined) {
              cleanedCompanies[key] = companiesData[key];
            }
          });

          // Add companies to values
          const valuesWithCompanies = { ...values, companies: cleanedCompanies };

          const cleanedValues = Utils.removeUndefined(valuesWithCompanies);

          const docRef = doc(
            firestore,
            FIRESTORE_REGIONS_TABLE,
            COMPANY_COUNTRY_CODE,
            FIRESTORE_COUNTRIES_TABLE,
            countryCode,
          );

          if (mode === ADD) {
            await setDoc(docRef, cleanedValues);
            message.success(t("notifications.country_added"));
            navigate(urls.countryList);
          } else {
            await updateDoc(docRef, cleanedValues);
            message.success(t("notifications.country_saved"));
          }
        } catch (err) {
          message.error(err.message);
        } finally {
          setSubmitLoading(false);
        }
      })
      .catch(() => {
        message.error(t("enter_required_data"));
      });
  };

  if (dataLoading) {
    return <Loading />;
  }

  return (
    <Form
      layout="vertical"
      form={form}
      name="country_form"
      className="ant-advanced-search-form"
      initialValues={{
        companies: {},
        country: {
          closedDates: [],
        },
        delivery: {
          cmrRequired: false,
          deliveryLabels: [],
        },
        pricing: {
          taxIncludedPrices: false,
        },
      }}
    >
      <PageHeaderAlt className="border-bottom" overlap>
        <div className="container">
          <Flex className="py-2" mobileFlex={false} justifyContent="between" alignItems="center">
            <h2 className="mb-3">
              {mode === ADD
                ? `${t("countries.add_country")}: ${countryName}`
                : mode === VIEW
                  ? `${t("countries.view_country")}: ${countryName}`
                  : `${t("countries.edit_country")}: ${countryName}`}
            </h2>
            <div className="mb-3">
              {mode === VIEW ? (
                <Button type="primary" onClick={() => navigate(urls.editCountry + countryCode)}>
                  {t("edit")}
                </Button>
              ) : (
                <Button type="primary" onClick={onFinish} htmlType="submit" loading={submitLoading}>
                  {mode === ADD ? t("add") : t("save")}
                </Button>
              )}
            </div>
          </Flex>
        </div>
      </PageHeaderAlt>
      <div className="container">
        <Tabs defaultActiveKey="1" style={{ marginTop: 30 }}>
          <TabPane tab={t("countries.delivery_tab")} key="1">
            <DeliveryField mode={mode} />
          </TabPane>
          <TabPane tab={t("countries.pricing_tab")} key="2">
            <PricingField mode={mode} />
          </TabPane>
          <TabPane tab={t("countries.contact_tab")} key="3">
            <CompaniesField mode={mode} />
          </TabPane>
          <TabPane tab={t("countries.dates_tab")} key="4">
            <DatesField mode={mode} />
          </TabPane>
        </Tabs>
      </div>
    </Form>
  );
};

export default CountryForm;
